import { SporsmalType } from '../../types/generated-types';
import { Kontaktinfofelt } from '../kontaktinfo/kontaktinfofelt';
import { CurrentSporsmal } from '../reducer/current-steg';
import { SkadelidtInfoSporsmal } from '../reducer/sporsmalsflyt-types';
import {
    MAKSLENGDER,
    validateFodselsnummer,
    validateInputLengde,
    validateIsNonEmptyText,
    validateMobilnummer,
} from '../validation/validate';

import '../kontaktinfo/kontaktinfo.scss';

export interface CurrentSkadelidtInfoSporsmal {
    type: SporsmalType.SkadelidtInfo;
    sporsmal: SkadelidtInfoSporsmal;
    fulltNavn: string;
    fodselsnummer: string;
    fastlegeKontor: string;
    hasBankIdOgMobil?: boolean;
    mobilnummer: string;
}

export type BesvartSkadelidtInfoSporsmal = CurrentSkadelidtInfoSporsmal;

const validateMobilnummerIfSporsmalAsked = (currenetSkadelidtInfoSporsmal: CurrentSkadelidtInfoSporsmal): boolean => {
    if (currenetSkadelidtInfoSporsmal.hasBankIdOgMobil) {
        return validateMobilnummer(currenetSkadelidtInfoSporsmal.mobilnummer);
    }
    return true;
};

export const isValid = (currenetSkadelidtInfoSporsmal: CurrentSkadelidtInfoSporsmal, type: Kontaktinfofelt) => {
    switch (type) {
        case Kontaktinfofelt.Navn:
            return validateIsNonEmptyText(currenetSkadelidtInfoSporsmal.fulltNavn);
        case Kontaktinfofelt.Fodselsnummer:
            return validateFodselsnummer(currenetSkadelidtInfoSporsmal.fodselsnummer);
        case Kontaktinfofelt.Adresse:
            return (
                validateIsNonEmptyText(currenetSkadelidtInfoSporsmal.fastlegeKontor) &&
                validateInputLengde(MAKSLENGDER.lokasjon, currenetSkadelidtInfoSporsmal.fastlegeKontor)
            );
        case Kontaktinfofelt.Mobilnummer:
            return validateMobilnummerIfSporsmalAsked(currenetSkadelidtInfoSporsmal);
        default:
            return true;
    }
};

export const isSkadelidtInfoSporsmal = (
    currentSporsmal: CurrentSporsmal,
): currentSporsmal is CurrentSkadelidtInfoSporsmal => currentSporsmal.type === SporsmalType.SkadelidtInfo;

export const updateSkadelidtInfoField = (
    currentSporsmal: CurrentSkadelidtInfoSporsmal,
    field: Kontaktinfofelt,
    svar: string,
): CurrentSkadelidtInfoSporsmal => {
    switch (field) {
        case Kontaktinfofelt.Navn:
            return { ...currentSporsmal, fulltNavn: svar };
        case Kontaktinfofelt.Fodselsnummer:
            return { ...currentSporsmal, fodselsnummer: svar.trim() };
        case Kontaktinfofelt.Adresse:
            return { ...currentSporsmal, fastlegeKontor: svar };
        case Kontaktinfofelt.Mobilnummer:
            return { ...currentSporsmal, mobilnummer: svar.trim() };
        default:
            return currentSporsmal;
    }
};

export const updateSkadelidtInfo = (
    currentSporsmal: CurrentSporsmal,
    field: Kontaktinfofelt,
    svar: string,
): CurrentSporsmal => {
    if (isSkadelidtInfoSporsmal(currentSporsmal)) {
        return updateSkadelidtInfoField(currentSporsmal, field, svar);
    }
    return currentSporsmal;
};

export const updateSkadelidtInfoHasBankIdOgMobil = (
    currentSporsmal: CurrentSporsmal,
    svar: boolean,
): CurrentSporsmal => {
    if (isSkadelidtInfoSporsmal(currentSporsmal)) {
        return {
            ...currentSporsmal,
            hasBankIdOgMobil: svar,
        };
    }

    return currentSporsmal;
};

export const isValidHasBankIdOgMobil = (currentSkadelidteInfoSporsmal: CurrentSkadelidtInfoSporsmal) => {
    return typeof currentSkadelidteInfoSporsmal.hasBankIdOgMobil === 'boolean';
};

export const isSkadelidtInfoSporsmalValid = (currentSporsmal: CurrentSkadelidtInfoSporsmal) => {
    return (
        isValid(currentSporsmal, Kontaktinfofelt.Navn) &&
        isValid(currentSporsmal, Kontaktinfofelt.Fodselsnummer) &&
        isValid(currentSporsmal, Kontaktinfofelt.Adresse) &&
        isValidHasBankIdOgMobil(currentSporsmal) &&
        isValid(currentSporsmal, Kontaktinfofelt.Mobilnummer)
    );
};
